import { useState } from 'react';
import { useForm, useController, useFormState, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import moment from 'moment';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
  Alert,
  Box,
  Button,
  DatePicker,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  TimeField,
  Typography,
} from '../../ui/core';
import ConfirmModal from '../../components/shared/ConfirmModal';
import { useToast } from '../../hooks';
import useCities from '../../hooks/useCities';
import api from '../../lib/api';
import { formatWindowTime } from './throttleFormatters';

const REASON_OPTIONS = [
  { value: 'WEATHER', label: 'Weather' },
  { value: 'DELAY', label: 'Delay' },
];

const DIRECTION_OPTIONS = [
  { value: 'BOTH', label: 'Both' },
  { value: 'DESTINATION', label: 'Into' },
  { value: 'ORIGIN', label: 'Out of' },
];

const PRODUCT_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'PRIVATE', label: 'Private' },
  { value: 'SHARED', label: 'Shared' },
];

const WINDOW_MODES = [
  { value: 'ALL_DAY', label: 'All day' },
  { value: 'BEFORE', label: 'Before' },
  { value: 'AFTER', label: 'After' },
  { value: 'BETWEEN', label: 'Between' },
];

// Words/phrases used to compose the plain-English confirm-dialog sentence,
// distinct from any table-display labels since the sentence reads naturally
// only with these exact fragments (e.g. "" for ALL rather than "All trips").
const PRODUCT_CONFIRM_WORDS = { PRIVATE: 'private ', SHARED: 'shared ', ALL: '' };
const DIRECTION_CONFIRM_PHRASES = { DESTINATION: 'into', ORIGIN: 'out of', BOTH: 'into and out of' };
// The pause only blocks *new* trip creation, so existing trips already on the
// books stay unaffected — but "existing shared trips" only makes sense to say
// out loud for a SHARED-scoped pause (matches the reviewed mockup's exact
// phrasing there); PRIVATE/ALL get a scope-accurate version of the same idea.
const EXISTING_TRIPS_CLAUSES = {
  SHARED: 'Riders can still book seats on existing shared trips.',
  PRIVATE: 'Existing private trips are unaffected.',
  ALL: 'Existing trips are unaffected.',
};

const defaultValues = {
  cityId: '',
  serviceDate: null,
  direction: 'BOTH',
  productScope: 'ALL',
  reason: '',
  reasonNote: '',
  windowMode: 'ALL_DAY',
  windowStart: null,
  windowEnd: null,
};

const isValidMoment = (value) => !!value && moment.isMoment(value) && value.isValid();

// `serviceDate`, `windowStart` and `windowEnd` are all `.nullable()` because
// `defaultValues` seeds them as `null` and the time fields reset to `null`.
// yup 1.x made `mixed()` non-nullable by default, so without this the form can
// never become valid from its own defaults — it fails on "windowEnd cannot be
// null" before any of the `.test()`s below get a say. Required-ness for these
// fields is enforced by those tests (conditionally, per `windowMode`), not by
// nullability.
export const schema = yup.object().shape({
  cityId: yup.string().required('City is required'),
  serviceDate: yup
    .mixed()
    .nullable()
    .test('required', 'Service date is required', (value) => !!value)
    .test(
      'not-past',
      'Service date must be today or later',
      (value) => !value || moment(value).isSameOrAfter(moment(), 'day'),
    ),
  direction: yup.string().required('Direction is required'),
  productScope: yup.string().required('Product is required'),
  reason: yup.string().required('Reason is required'),
  reasonNote: yup.string().notRequired(),
  windowMode: yup
    .string()
    .oneOf(['ALL_DAY', 'BEFORE', 'AFTER', 'BETWEEN'])
    .test('window-origin-only', 'Window only allowed for Out of', function (value) {
      const { direction } = this.parent;
      const isAllDay = !value || value === 'ALL_DAY';
      return isAllDay || direction === 'ORIGIN';
    }),
  windowStart: yup
    .mixed()
    .nullable()
    .test('window-start-required', 'Start time is required', function (value) {
      const { windowMode } = this.parent;
      if (windowMode !== 'AFTER' && windowMode !== 'BETWEEN') return true;
      return isValidMoment(value);
    }),
  windowEnd: yup
    .mixed()
    .nullable()
    .test('window-end-required', 'End time is required', function (value) {
      const { windowMode } = this.parent;
      if (windowMode !== 'BEFORE' && windowMode !== 'BETWEEN') return true;
      return isValidMoment(value);
    })
    .test('window-end-after-start', 'End must be after start', function (value) {
      const { windowMode, windowStart } = this.parent;
      if (windowMode !== 'BETWEEN') return true;
      if (!isValidMoment(value) || !isValidMoment(windowStart)) return true;
      return moment(value).isAfter(moment(windowStart));
    }),
});

const CityField = ({ control }) => {
  const { field, fieldState } = useController({ name: 'cityId', control });
  const { cities } = useCities();

  return (
    <FormControl fullWidth error={!!fieldState.error}>
      <InputLabel id="trip-throttle-city-label">City</InputLabel>
      <Select
        labelId="trip-throttle-city-label"
        label="City"
        value={field.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
      >
        {(cities || []).map((city) => (
          <MenuItem key={city.id} value={city.id}>
            {city.cityName}
          </MenuItem>
        ))}
      </Select>
      {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
    </FormControl>
  );
};

const ServiceDateField = ({ control }) => {
  const { field, fieldState } = useController({ name: 'serviceDate', control });

  return (
    <Box>
      <DatePicker
        label="Service day"
        value={field.value}
        onChange={field.onChange}
        moment
        minDate={moment()}
        fullWidth
      />
      {fieldState.error && <FormHelperText error>{fieldState.error.message}</FormHelperText>}
    </Box>
  );
};

const ReasonField = ({ control }) => {
  const { field, fieldState } = useController({ name: 'reason', control });

  return (
    <FormControl fullWidth error={!!fieldState.error}>
      <InputLabel id="trip-throttle-reason-label">Reason</InputLabel>
      <Select
        labelId="trip-throttle-reason-label"
        label="Reason"
        value={field.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
      >
        {REASON_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
    </FormControl>
  );
};

const NoteField = ({ control }) => {
  const { field, fieldState } = useController({ name: 'reasonNote', control });

  return (
    <TextField
      fullWidth
      label="Note"
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={!!fieldState.error}
      helperText={fieldState.error ? fieldState.error.message : 'Optional'}
    />
  );
};

const DirectionField = ({ control, setValue }) => {
  const { field } = useController({ name: 'direction', control });

  return (
    <ToggleButtonGroup
      exclusive
      fullWidth
      value={field.value}
      onChange={(event, value) => {
        if (value === null) return;
        field.onChange(value);
        // A window is only ever valid for an ORIGIN ("Out of") throttle, so
        // switching away from it must clear any window the user had set —
        // otherwise a stray window could ride along on a Into/Both submit.
        if (value !== 'ORIGIN') {
          setValue('windowMode', 'ALL_DAY', { shouldValidate: true });
          setValue('windowStart', null, { shouldValidate: true });
          setValue('windowEnd', null, { shouldValidate: true });
        }
      }}
      aria-label="Direction"
    >
      {DIRECTION_OPTIONS.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

const ProductField = ({ control }) => {
  const { field } = useController({ name: 'productScope', control });

  return (
    <ToggleButtonGroup
      exclusive
      fullWidth
      value={field.value}
      onChange={(event, value) => value !== null && field.onChange(value)}
      aria-label="Products"
    >
      {PRODUCT_OPTIONS.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

// Only ever meaningful for ORIGIN ("Out of") throttles — renders nothing for
// Into/Both so a window can never be set (or shown) outside that direction.
const WindowField = ({ control }) => {
  const direction = useWatch({ control, name: 'direction' });
  const { field: modeField } = useController({ name: 'windowMode', control });
  const { field: startField, fieldState: startFieldState } = useController({ name: 'windowStart', control });
  const { field: endField, fieldState: endFieldState } = useController({ name: 'windowEnd', control });

  if (direction !== 'ORIGIN') return null;

  const error = startFieldState.error || endFieldState.error;

  return (
    <Box>
      <FieldLabel>Window</FieldLabel>
      <ToggleButtonGroup
        exclusive
        fullWidth
        value={modeField.value}
        onChange={(event, value) => value !== null && modeField.onChange(value)}
        aria-label="Window"
      >
        {WINDOW_MODES.map((option) => (
          <ToggleButton key={option.value} value={option.value}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {modeField.value !== 'ALL_DAY' && (
        <Stack direction="row" spacing="8px" alignItems="center" sx={{ mt: '16px' }}>
          {modeField.value === 'BEFORE' && (
            <TimeField label="Before" value={endField.value} onChange={endField.onChange} fullWidth />
          )}
          {modeField.value === 'AFTER' && (
            <TimeField label="After" value={startField.value} onChange={startField.onChange} fullWidth />
          )}
          {modeField.value === 'BETWEEN' && (
            <>
              <TimeField label="Start" value={startField.value} onChange={startField.onChange} fullWidth />
              <Typography>–</Typography>
              <TimeField label="End" value={endField.value} onChange={endField.onChange} fullWidth />
            </>
          )}
        </Stack>
      )}
      {error && <FormHelperText error>{error.message}</FormHelperText>}
    </Box>
  );
};

const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: '10px',
    }}
  >
    {children}
  </Typography>
);

// Plain-English restatement for the confirm dialog, matching the reviewed
// mockup's exact phrasing for a SHARED-scoped pause (e.g. "Pause all new
// shared trips into Austin on Wed Jul 22. Riders can still book seats on
// existing shared trips. Takes effect within about a minute."). The "about a
// minute" line is the enforcement cache TTL — keep it, it's not a generic
// disclaimer.
// '' for ALL_DAY (sentence unchanged) so the day-phrase below stays
// byte-identical to the pre-window copy; otherwise a trailing-space fragment
// ("before 4:00pm ") inserted right before "on <day>".
const windowPhrase = (windowMode, windowStart, windowEnd) => {
  if (windowMode === 'BEFORE') return `before ${formatWindowTime(windowEnd)} `;
  if (windowMode === 'AFTER') return `after ${formatWindowTime(windowStart)} `;
  if (windowMode === 'BETWEEN') return `${formatWindowTime(windowStart)}–${formatWindowTime(windowEnd)} `;
  return '';
};

export const scopeSummary = ({
  cityName,
  serviceDate,
  direction,
  productScope,
  windowMode,
  windowStart,
  windowEnd,
}) => {
  const day = moment(serviceDate).format('ddd MMM D');
  const productWord = PRODUCT_CONFIRM_WORDS[productScope] ?? '';
  const directionPhrase = DIRECTION_CONFIRM_PHRASES[direction] || '';
  const existingTripsClause = EXISTING_TRIPS_CLAUSES[productScope] || EXISTING_TRIPS_CLAUSES.ALL;
  const windowFragment = windowPhrase(windowMode, windowStart, windowEnd);

  return `Pause all new ${productWord}trips ${directionPhrase} ${cityName} ${windowFragment}on ${day}. ${existingTripsClause} Takes effect within about a minute.`;
};

// Pure mapping from the submitted form shape to the create endpoint's input
// shape — split out so the date formatting can be tested without rendering
// the form.
const hhmm = (value) => (value ? moment(value).format('HH:mm') : null);

export const buildCreateInput = ({
  cityId,
  serviceDate,
  direction,
  productScope,
  reason,
  reasonNote,
  windowMode = 'ALL_DAY',
  windowStart,
  windowEnd,
}) => ({
  cityId,
  serviceDate: moment(serviceDate).format('YYYY-MM-DD'),
  direction,
  productScope,
  reason,
  reasonNote: reasonNote || '',
  // Half-open per backend contract: the unset side of a window is omitted/
  // null, never '00:00'/'23:59' — and ALL_DAY omits both keys entirely.
  ...(windowMode === 'ALL_DAY'
    ? {}
    : {
        windowStart: windowMode === 'BEFORE' ? null : hhmm(windowStart),
        windowEnd: windowMode === 'AFTER' ? null : hhmm(windowEnd),
      }),
});

const CreateThrottlePanel = ({ onCreated }) => {
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [serverError, setServerError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const { cities } = useCities();
  const toast = useToast();

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { isValid, isSubmitting } = useFormState({ control: form.control });

  const onValid = (values) => {
    setServerError(null);
    const city = (cities || []).find((c) => c.id === values.cityId);
    setPendingSubmission({ ...values, cityName: city ? city.cityName : values.cityId });
  };

  const onCancelConfirm = () => setPendingSubmission(null);

  const onConfirm = async () => {
    setConfirming(true);
    try {
      await api.tripThrottling.createTripThrottle(buildCreateInput(pendingSubmission));
      setPendingSubmission(null);
      toast.success('Trip throttle paused.');
      // Ops pauses several days for one city, or several cities for one day,
      // one combo at a time — keep the city selected and clear the rest so
      // the panel is immediately ready for the next combo instead of forcing
      // a full re-pick.
      form.reset({ ...defaultValues, cityId: pendingSubmission.cityId });
      onCreated && (await onCreated());
    } catch (error) {
      setPendingSubmission(null);
      setServerError(error.message);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Box>
      <Box component="form" onSubmit={form.handleSubmit(onValid)}>
        <Stack spacing="24px">
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <FieldLabel>City</FieldLabel>
              <CityField control={form.control} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FieldLabel>Service day</FieldLabel>
              <ServiceDateField control={form.control} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FieldLabel>Reason</FieldLabel>
              <ReasonField control={form.control} />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FieldLabel>Direction</FieldLabel>
              <DirectionField control={form.control} setValue={form.setValue} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FieldLabel>Products</FieldLabel>
              <ProductField control={form.control} />
            </Grid>
          </Grid>

          <WindowField control={form.control} />

          <Box>
            <FieldLabel>Note</FieldLabel>
            <NoteField control={form.control} />
          </Box>

          {serverError && <Alert severity="error">{serverError}</Alert>}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="submit" variant="contained" disabled={!isValid || isSubmitting}>
              {isSubmitting ? 'Submitting…' : 'Pause new trips'}
            </Button>
          </Box>
        </Stack>
      </Box>

      <ConfirmModal
        open={!!pendingSubmission}
        title="Pause new trips?"
        message={pendingSubmission ? scopeSummary(pendingSubmission) : ''}
        confirmLabel={confirming ? 'Pausing…' : 'Confirm pause'}
        confirmColor="primary"
        confirmDisabled={confirming}
        onConfirm={onConfirm}
        onCancel={onCancelConfirm}
      />
    </Box>
  );
};

export default CreateThrottlePanel;
